import React, { useState, useEffect } from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react';
import { useTooltip, useTooltipInPortal, defaultStyles } from '@visx/tooltip';
import { ISchemaLevelObject, ISchemaLevelObjectType, ISchemaLevelObjectsResponse } from 'utils/types';
import { localPoint } from '@visx/event';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectSnowflakeAccount } from 'app/snowflakeSlice';
import {
  selectSelectedDatabases,
  selectSelectedSchemas,
  selectSelectedSchemaLevelObjects,
  selectSchema,
  unselectSchema,
  addSchemaLevelObjects,
} from '../reducers/builderSlice';
import { useGetSchemaLevelObjectsQuery } from 'app/createApi';
import ObjectItem from './ObjectItem';
import { classNames } from 'utils/styleUtils';

const tooltipStyles = {
  ...defaultStyles,
  backgroundColor: '#4b5563',
  color: 'white',
  fontSize: 12,
  zIndex: 30,
  maxWidth: 500,
};

interface TooltipData {
  description: string;
}

interface SchemaItemProps {
  databaseName: string;
  schemaName: string;
  mode: 'selection' | 'preview';
  highlighted?: boolean;
  objectTypes: ISchemaLevelObjectType[];
}

export default function SchemaItem(props: SchemaItemProps): JSX.Element {
  const dispatch = useAppDispatch();
  const snowflakeAccount = useAppSelector(selectSnowflakeAccount);
  const selectedDatabases = useAppSelector(selectSelectedDatabases);
  const selectedSchemas = useAppSelector(selectSelectedSchemas);
  const selectedSchemaLevelObjects = useAppSelector(selectSelectedSchemaLevelObjects);

  // Only fetch the objects if the schema is expanded
  const [isOpen, setIsOpen] = useState(false);
  const { data: schemaLevelObjects, isLoading } = useGetSchemaLevelObjectsQuery(
    {
      account: snowflakeAccount,
      database: props.databaseName,
      schema: props.schemaName,
      object_types: props.objectTypes,
    },
    { skip: !isOpen },
  );

  const [filteredObjects, setFilteredObjects] = useState<ISchemaLevelObjectsResponse>({});
  const [selected, setSelected] = useState(false);

  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } = useTooltip<TooltipData>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  });

  useEffect(() => {
    if (schemaLevelObjects !== undefined) {
      // Flatten all objects for the addTables action
      const allObjects: ISchemaLevelObject[] = [];
      Object.entries(schemaLevelObjects).forEach(([key, objects]) => {
        objects.forEach((object) => {
          allObjects.push({
            database: props.databaseName,
            schema: props.schemaName,
            name: object,
            type: key as ISchemaLevelObjectType,
          });
        });
      });

      const schemaLevelObjectsArray = allObjects.map((obj) => {
        return { database: props.databaseName, schema: props.schemaName, name: obj.name, type: obj.type };
      });
      dispatch(
        addSchemaLevelObjects({
          schemaKey: `${props.databaseName}.${props.schemaName}`,
          schemaLevelObjects: schemaLevelObjectsArray,
        }),
      );
    }
  }, [schemaLevelObjects]);

  // In preview mode, only show the selected tables
  useEffect(() => {
    if (schemaLevelObjects !== undefined) {
      const filtered: ISchemaLevelObjectsResponse = {};

      if (props.mode === 'selection') {
        // In selection mode, show all objects
        Object.entries(schemaLevelObjects).forEach(([objectTypes, objects]) => {
          filtered[objectTypes] = objects;
        });
      } else {
        // In preview mode, only show selected objects
        Object.entries(schemaLevelObjects).forEach(([objectType, objects]) => {
          filtered[objectType] = objects.filter(
            (objectName) =>
              selectedSchemaLevelObjects.some(
                (selected) =>
                  selected.database === props.databaseName &&
                  selected.schema === props.schemaName &&
                  selected.name === objectName &&
                  selected.type === objectType,
              ) ||
              selectedSchemas.some(
                (selected) => selected.database === props.databaseName && selected.schema === props.schemaName,
              ) ||
              selectedDatabases.some((selected) => selected.database === props.databaseName),
          );
        });
      }

      setFilteredObjects(filtered);
    }
  }, [schemaLevelObjects, selectedDatabases, selectedSchemas, selectedSchemaLevelObjects]);

  useEffect(() => {
    const databaseSelected = selectedDatabases.find((database) => database.database === props.databaseName);
    const schemaSelected = selectedSchemas.find(
      (schema) => schema.schema === props.schemaName && schema.database === props.databaseName,
    );
    if (databaseSelected !== undefined || schemaSelected !== undefined) {
      setSelected(true);
    } else {
      setSelected(false);
    }
  }, [selectedDatabases, selectedSchemas]);

  const onClick = (): void => {
    if (selected) {
      dispatch(unselectSchema({ schema: props.schemaName, database: props.databaseName }));
    } else {
      dispatch(selectSchema({ schema: props.schemaName, database: props.databaseName }));
    }
    setSelected(!selected);
  };

  return (
    <>
      <Disclosure>
        {({ open }) => (
          <>
            <div
              className={classNames(
                'w-full relative flex py-1 border border-gray-300 bg-gray-50 rounded-md text-gray-700 cursor-default',
                props.mode === 'selection' ? 'px-4' : 'px-2',
              )}
            >
              {props.mode === 'selection' && (
                <div className="absolute left-2">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onClick()}
                    className="rounded border-gray-300 cursor-pointer"
                  />
                </div>
              )}
              <div
                className={classNames('min-w-[20px] w-5 mr-1', props.mode === 'selection' ? 'ml-2' : '')}
                ref={containerRef}
              >
                {props.highlighted === true ? (
                  <img className="w-5" src="/static/schema-icon-solid.svg" alt="Schema" />
                ) : (
                  <img className="w-5" src="/static/schema-icon.svg" alt="Schema" />
                )}
              </div>
              <div
                className="text-sm truncate mr-2"
                onMouseLeave={() => {
                  hideTooltip();
                }}
                onMouseMove={(event) => {
                  const eventSvgCoords = localPoint(event);
                  showTooltip({
                    tooltipData: { description: props.schemaName },
                    tooltipTop: eventSvgCoords?.y !== undefined ? eventSvgCoords.y + 10 : undefined,
                    tooltipLeft: eventSvgCoords?.x,
                  });
                }}
              >
                {props.schemaName}
              </div>
              <DisclosureButton className="absolute right-1 w-5 cursor-pointer">
                <ChevronRightIcon
                  onClick={() => setIsOpen(!isOpen)}
                  className={classNames(
                    open ? 'transform rotate-90' : '',
                    'w-5 h-5 ml-auto text-gray-700 transition duration-150 ease-in-out',
                  )}
                />
              </DisclosureButton>
            </div>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacitsy-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <DisclosurePanel className="px-4 space-y-1">
                {!isLoading &&
                  Object.entries(filteredObjects).map(([objectType, objects]) => (
                    <React.Fragment key={objectType}>
                      {objects.map((objectName: string) => (
                        <ObjectItem
                          key={`${objectType}-${objectName}`}
                          databaseName={props.databaseName}
                          schemaName={props.schemaName}
                          objectName={objectName}
                          objectType={objectType as ISchemaLevelObjectType}
                          mode={props.mode}
                          highlighted={props.highlighted}
                        />
                      ))}
                    </React.Fragment>
                  ))}
                {isLoading && (
                  <div className="w-full h-[30px] border border-gray-300 rounded-md py-1 px-2">
                    <div className="animate-pulse h-2 bg-slate-300 rounded mt-[6px]"></div>
                  </div>
                )}
                {!isLoading && Object.values(filteredObjects).every((arr) => arr.length === 0) && (
                  <div className="mb-2 text-sm text-gray-500">No objects found</div>
                )}
              </DisclosurePanel>
            </Transition>
          </>
        )}
      </Disclosure>
      {tooltipOpen && tooltipData !== undefined && (
        <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
          <div className="text-sm">{tooltipData.description}</div>
        </TooltipInPortal>
      )}
    </>
  );
}
